import React from 'react';
import moment from 'moment';
import R, {
  merge,
  __,
  pathOr,
  reject,
  chain,
  sort,
  cond,
  lt,
  gt,
  always,
  T,
  equals,
  length,
  min,
  prop,
  values,
  ifElse,
  max,
  compose,
  head,
  map,
  last,
  both,
  complement,
  call,
  identity,
  append,
  addIndex,
  reduce,
  and,
  omit,
  construct,
  apply,
  useWith,
  nthArg,
  curry,
  all,
  any,
  propOr,
  find,
  isEmpty,
  concat,
} from 'ramda';
const neither = complement(and);
const sortByDateAsc = sort(cond([
  [lt, always(-1)],
  [gt, always(1)],
  [T, always(0)],
]));
const NewDate = date => new Date(date);
const getStartDate = obj => {
  const start = obj.startDate || obj.seasonStartDate || obj.fromDate;
  if (start) return new Date(start);
  // below block is legacy, might not get to be called
  // but not 100% sure
  return call(
    compose(
      ifElse(
        identity,
        construct(NewDate),
        always(null)
      ),
      ifElse(
        compose(equals(2), length),
        apply(min),
        prop(0)
      ),
      values,
      omit(['_id', 'eventName'])
    ),
    obj
  );
};
const getEndDate = obj => {
  const end = obj.endDate || obj.seasonEndDate || obj.toDate || obj.startDate;
  if (end) return new Date(end);
  return call(
    compose(
      ifElse(
        identity,
        construct(NewDate),
        always(null)
      ),
      ifElse(
        compose(equals(2), length),
        apply(max),
        prop(0)
      ),
      values,
      omit(['_id', 'eventName'])
    ), obj
  );
};

const getFirstDayFromAnArrayOfRanges = compose(
  head,
  sortByDateAsc,
  map(getStartDate)
);

const getLastDayFromAnArrayOfRanges = compose(
  last,
  sortByDateAsc,
  map(getEndDate)
);

const sortDateRangeAsc = sort(cond([
  [useWith(lt, [getStartDate, getStartDate]), always(-1)],
  [useWith(gt, [getStartDate, getStartDate]), always(1)],
  [T, always(0)],
]));
const notEqual = complement(equals);

const getMissingRangesReducerFn = (missingRanges, currentRange, index, ranges) => {
  if (!index) return missingRanges;
  const currentStartDate = getStartDate(currentRange);
  const yesterday = moment.utc(currentStartDate).subtract(1, 'days');
  const lastEndDate = getEndDate(ranges[index - 1]);
  const tomorrow = moment.utc(lastEndDate).add(1, 'days');
  return call(ifElse(
    () => moment.utc(lastEndDate).isBefore(yesterday, 'day'),
    append({ from: tomorrow, to: yesterday }),
    identity
  ), missingRanges);
};

const getMissingRanges = (allRanges, contractingPeriod) => call(compose(
  addIndex(reduce)(getMissingRangesReducerFn, []),
  sortDateRangeAsc,
  () => addContractingPeriodToDateRanges(allRanges, contractingPeriod)
));
const dateToString = date => moment.utc(date).format('DD MMM YYYY');

const notInContractingPeriod = (seasons, start, end) => {
  const seasonFirst = getFirstDayFromAnArrayOfRanges(seasons);
  const seasonLast = getLastDayFromAnArrayOfRanges(seasons);
  if (neither(
    moment.utc(seasonFirst).isBetween(start, end, 'day', '[]'),
    moment.utc(seasonLast).isBetween(start, end, 'day', '[]'))
  ) {
    return `Your seasons, which start from ${dateToString(seasonFirst)} - ${dateToString(seasonLast)},
    are not within your contract validity period.`;
  }
  return '';
};

const	_getDateFromUTC = date => {
  const dateObject = new Date(date);
  return new Date(dateObject.getUTCFullYear(), dateObject.getUTCMonth(), dateObject.getUTCDate());
};
const transformDateRanges = dateRanges => {
  return dateRanges.map(dateRange => {
    return Object.keys(dateRange).reduce((result, key) => {
      if (key.indexOf('tartDate') > 0) result.from = _getDateFromUTC(dateRange[key]);
      if (key.indexOf('ndDate') > 0) result.to = _getDateFromUTC(dateRange[key]);
      return result;
    }, {});
  });
};
const sameDay = (d1, d2) => moment(d1).isSame(d2, 'day');
const datesMatch = curry((range1, range2) => sameDay(getStartDate(range1), getStartDate(range2)) && sameDay(getEndDate(range1), getEndDate(range2)));
const existsIn = curry((rangesFromSeason, range) => any(datesMatch(range), rangesFromSeason));
const checkIfSameSeason = curry((ranges, rangesFromSeason) => and(all(existsIn(rangesFromSeason), ranges), all(existsIn(ranges), rangesFromSeason)));
const getSeasonNameIfMatch = curry((seasons, ranges) => call(compose(
  propOr('', 'seasonName'),
  find(compose(checkIfSameSeason(ranges), propOr([], 'dateRanges')))
), seasons));
const displayRange = (range, classes = {}) => {
  const start = getStartDate(range);
  const end = getEndDate(range);
  return sameDay(start, end) ?
    <span className={classes.start}>{dateToString(start)}</span>
    : (<span>
      <span className={classes.start}>{dateToString(start)}</span> - <span className={classes.end}>{dateToString(end)}</span>
    </span>);
};
const displayDateRanges = (dateRanges) => dateRanges.map((range, index) => {
  return (
    <div key={index}>
      {
        displayRange(range)
      }
    </div>);
});

const addContractingPeriodToDateRanges = (dateRanges, contractingPeriod) => concat(dateRanges, [{
  seasonStartDate: moment.utc(contractingPeriod.startDate).subtract(1, 'days'),
  seasonEndDate: moment.utc(contractingPeriod.startDate).subtract(1, 'days'),
}, {
  seasonStartDate: moment.utc(contractingPeriod.endDate).add(1, 'days'),
}]);
const seasonCovered = (dateRanges, contractingPeriod) => {
  const dateRangesWithStartAndEnd = addContractingPeriodToDateRanges(dateRanges, contractingPeriod);
  return !notInContractingPeriod(dateRanges, getStartDate(contractingPeriod), getEndDate(contractingPeriod)) && isEmpty(getMissingRanges(dateRangesWithStartAndEnd, contractingPeriod));
};
const isSeasonSameWithContractingPeriod = (dateRanges, contractingPeriod) => datesMatch(contractingPeriod, pathOr({}, [0, 0], dateRanges));

// const matchAllocationOrPolicyWithSeasons = (seasons, contents) => addIndex(map)(ifElse(
//   (season, index) => checkIfSameSeason(prop('dateRanges', season), pathOr([{}], [index, 'dateRanges'], contents)),
//     compose(propOr({}, __, contents), nthArg(1)),
//   (season, index) => {
//     if (index < contents.length) return merge({ correctDateRanges: prop('dateRanges', season), correctSeasonName: prop('seasonName', season) }, contents[index]);
//     return omit(['_id', 'seasonName'], season);
//   }
// ), seasons);
const matchAllocationOrPolicyWithSeasons = (seasons, contents) => seasons.map((season, i) => {
  // for allocation, product cancellation, pickup, dropoff
  if (contents[i]) return merge(contents[i], { dateRanges: season.dateRanges });
  return { dateRanges: season.dateRanges };
});

const dayInRange = curry((day, { seasonStartDate, seasonEndDate }) => seasonStartDate && moment.utc(day).isBetween(seasonStartDate, seasonEndDate || seasonStartDate, 'day', []))
const dayInDateRanges = curry((day, dateRanges) => {
  return dateRanges.some(dayInRange(day));
});
const dayInSeason = curry((day, seasons) => seasons.some(({ dateRanges }) => dayInDateRanges(day, dateRanges)));

export default {
  sortDateRangeAsc,
  getMissingRanges,
  notInContractingPeriod,
  dateToString,
  transformDateRanges,
  datesMatch,
  checkIfSameSeason,
  getSeasonNameIfMatch,
  displayDateRanges,
  addContractingPeriodToDateRanges,
  seasonCovered,
  isSeasonSameWithContractingPeriod,
  isDateRangesWithEmptyDates: dateRanges => !(dateRanges && dateRanges.length && dateRanges.some(range => range.seasonStartDate)),
  getFlattenedUnEmptyDates: compose(
    reject(isEmpty),
    chain(propOr([], 'dateRanges'))
  ),
  matchAllocationOrPolicyWithSeasons,
  checkIfFullSeasonCoveredForAProduct: (start, end, arrayOfDates) => {
    if (!arrayOfDates.length) return 1;
    const daysInSeason = Math.abs(moment(start).diff(moment(end), 'days')) + 1;
    const numOfDays = arrayOfDates.map(obj => {
      if (!(getStartDate(obj) && getEndDate(obj))) return 0;
      const rangeStart = moment(getStartDate(obj)).add(1, 'd');
      const rangeEnd = moment(getEndDate(obj)).add(1, 'd');
      return Math.round(moment(rangeEnd).diff(moment(rangeStart), 'days', true) + 1);
    });
    const totalNumOfDaysCovered = numOfDays.reduce((a, b) => a + b);
    return daysInSeason - totalNumOfDaysCovered;
  },
  _getDateFromUTC: date => {
    if (!date) return date;
    const dateObject = new Date(date);
    return new Date(dateObject.getUTCFullYear(), dateObject.getUTCMonth(), dateObject.getUTCDate());
  },
  dayInDateRanges,
  dayInSeason,
  getFirstDayFromAnArrayOfRanges,
  displayRange,
  overlapExisting: ({ from, to }, existingSeasons) => {
    if (!(from && to)) return false;
    return existingSeasons.some(({ dateRanges }) => dateRanges.some(range => {
      return range.seasonStartDate
        && (
          dayInRange(range.seasonStartDate, { seasonStartDate: from, seasonEndDate: to })
          || dayInRange(range.seasonEndDate, { seasonStartDate: from, seasonEndDate: to })
          || dayInRange(from, range)
          || dayInRange(to, range)
        );
    }));
    // return (dayInSeason(from, existingSeasons) || dayInSeason(to, existingSeasons));
  },
  removeOverlapDates: ({ from, to }, existingSeasons) => {
    const removeOneRangeAFromRangeB = R.curry((rangeA, rangeB) => {
      const startDate = rangeB.seasonStartDate;
      const endDate = rangeB.seasonEndDate;
      if (!rangeA || !startDate) return [rangeB];
      const rangeAStart = moment.utc(rangeA.from).format();
      const rangeAEnd = moment.utc(rangeA.to || rangeA.from).format();
      let dateRangesToReturn = [];
      if (moment.utc(rangeAEnd).isBefore(startDate, 'day') || moment.utc(rangeAStart).isAfter(endDate, 'day')) {
        dateRangesToReturn = [rangeB];
      } else if (moment.utc(rangeAStart).isSameOrAfter(startDate, 'day')) {
        if (moment.utc(rangeAEnd).isBefore(endDate, 'day')) {
          dateRangesToReturn = [
            {
              seasonStartDate: startDate,
              seasonEndDate: moment.utc(rangeAStart).subtract(1, 'days').format(),
            },
            {
              seasonStartDate: moment.utc(rangeAEnd).add(1, 'days').format(),
              seasonEndDate: endDate,
            },
          ];
        } else {
          dateRangesToReturn = [{
            seasonStartDate: startDate,
            seasonEndDate: moment.utc(rangeAStart).subtract(1, 'days').format(),
          }];
        }
      } else {
        if (moment.utc(rangeAEnd).isBetween(startDate, endDate, 'day', '[)')) {
          dateRangesToReturn = [{
            seasonStartDate: moment.utc(rangeAEnd).add(1, 'days').format(),
            seasonEndDate: endDate,
          }];
        } else { dateRangesToReturn = []; }
      }
      return dateRangesToReturn.filter(dateRangeToFilter => moment.utc(dateRangeToFilter.seasonEndDate).isSameOrAfter(dateRangeToFilter.seasonStartDate, 'day'));
    });
    return existingSeasons.map(season => {
      const dateRanges = R.chain(removeOneRangeAFromRangeB({ from, to }), season.dateRanges);
      return merge(season, { dateRanges });
    });
  },
};



// WEBPACK FOOTER //
// ./src/app/utils/date-utils.js